import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faUser, faPaperPlane, faCheckCircle, faTimesCircle, faInfoCircle, faPlus, faStar, faAngleLeft, faArrowLeft, faCircleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Input, MarkdownContainer } from '../../styles/PageStyles';
import { streamAgentQuery } from '../../services/kbQueryService';
import { v4 as uuid } from 'uuid';
// consolidated icon imports above

const CoPilotContainer = styled.div`
  /* width controlled by inline style (resizable) */
  height: calc(100vh - 126px);
  background: white;
  border-left: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative; /* needed for resize handle */
`;

const ResizeHandle = styled.div`
  position: absolute;
  left: -8px;
  top: 0;
  bottom: 0;
  width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ew-resize;
  z-index: 20;
  &:hover .bar { background: rgba(0,0,0,0.12); }
`;

const ResizeBar = styled.div`
  width: 4px;
  height: 40px;
  border-radius: 2px;
  background: rgba(0,0,0,0.06);
`;

const CoPilotHeader = styled.div`
  // background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%);
//   background: linear-gradient(
//   to bottom,
//   #90caf9 0%,   /* darker at top */
//   #e3f2fd 100% /* lighter at bottom */
// );

background: linear-gradient(
  to bottom,
  #9ccfff 0%,
  #eaf4ff 100%
);
  color: #333;
  padding: 1.25rem 1rem;
  // border-bottom: 1px solid #e0e0e0;
  border-radius: 12px 12px 0 0;
`;

const CoPilotTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.1rem;
  font-weight: 500;
  color: #135a91;
`;

const UserMessageContainer = styled.div`
  padding: 10px 14px;
  background: #1789fc;
  width: fit-content;
  max-width: 85%;
  color: #fff;
  margin-left: auto;
  margin-bottom: 1rem;
  border-radius: 8px;
  word-wrap: break-word;
  white-space: pre-wrap;
  min-height: fit-content;
  
  p {
    margin: 0;
    line-height: 1.5;
  }
  
  ul, ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }
  
  li {
    margin: 0.25rem 0;
  }
`

const TableScrollWrapper = styled.div`
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  
  table {
    min-width: 100%;
  }
`;

const StatusSection = styled.div`
  padding: 1.25rem 1rem;
  border-bottom: 1px solid #f0f0f0;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StatusIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
`;

const StatusContent = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-weight: 600;
  color: #1a1a1a;
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const StatusText = styled.div`
  color: #4a4a4a;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const HighlightedText = styled.span`
  font-weight: 600;
  color: #1a1a1a;
`;

const EvaluationSection = styled.div`
  padding: 1.25rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  background: #fcfcfc;
  gap: 22px;
  flex: 1;          
  min-height: 0;   
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 120px; /* ensure input area doesn't overlap streamed content */
`;

const EvaluationTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  color: #1a1a1a;
  font-size: 1rem;
  margin-bottom: 1rem;
`;

const EvaluationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const EvaluationItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #4a4a4a;
  line-height: 1.4;
`;

const BulletPoint = styled.div`
  width: 6px;
  height: 6px;
  background: #9e9e9e;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
`;

const IneligibilitySection = styled.div`
  padding: 1.25rem 1rem;
  border-bottom: 1px solid #f0f0f0;
`;

const ReasonBox = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 0.75rem;
`;

const DraftSection = styled.div`
  padding: 1.25rem 1rem;
  border: 2px solid #e3f2fd;
  border-radius: 8px;
  margin: 1rem;
  background: #fafbff;
`;

const ActionButton = styled.div`
  width: calc(100% - 2rem);
  margin: 0 1rem 1rem 1rem;
  position: relative;
  // padding: 1rem;
  background:
  linear-gradient(white, white) padding-box,
  linear-gradient(45deg, #2196F3, #9C27B0) border-box;
  border: 2px solid transparent;
  border-radius: 36px;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
  }
`;

const PlusIcon = styled(FontAwesomeIcon)`
  color: #2196f3;
  position: absolute;
  left: 10px;
  cursor: pointer;
  transition: color 0.2s ease, transform 0.2s ease;
`;

const SendIconButton = styled.button`
  position: absolute;
  right: 10px;
  background: transparent;
  border: none;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #2a6f97;
  border-radius: 50%;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;


// static sample messages removed — the component now renders messages and processing steps from the streaming API


const ThinkingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 12px;
  max-width: fit-content;
  margin: 12px 14px;
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  background: #64B5F6;
  border-radius: 50%;
  animation: blink 1.4s infinite both;

  &:nth-child(2) {
    animation-delay: 0.2s;
  }
  &:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes blink {
    0% { opacity: 0.2; }
    20% { opacity: 1; }
    100% { opacity: 0.2; }
  }
`;

const DelayMessage = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-left: 8px;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`; 

const LoadingSection = styled.div`
  display: flex;
  align-items: center;
  gap:10px
`;

export const InputTextarea = styled.textarea`
  width: 100%;
  min-height: 40px; /* default height */
  max-height: 200px;
  padding: 0.6rem 1rem; /* top/bottom smaller, left/right normal */
  background: #ffffff;
  border: 1px solid #0066ff;
  border-radius: 36px;
  color: var(--text-primary);
  font-size: 0.9rem;
  line-height: 1.5rem; /* vertical centering */
  resize: none;
  overflow: hidden;

  &::placeholder {
    color: #a0a0a0;
    line-height: 1.5rem; /* match line-height of input */
  }

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1);
  }
`;

export const ChatThinkingLoader = () => (
  <ThinkingWrapper>
    <Dot />
    <Dot />
    <Dot />
  </ThinkingWrapper>
);

// const configMessage = {
//     "user_response": "## Claim Request Data for Maria Lopez\n\n| Claim Request ID | Email Thread ID          | Claim Type       | Reported Condition                                              | Diagnosis Date |\n|-------------------|--------------------------|------------------|-----------------------------------------------------------------|----------------|\n| CR-CI001-3       | EMAIL-CI001-20260115-01 | Critical Illness | Acute myocardial infarction with persistent left ventricular dysfunction | 2026-01-10    |\n\nLet me know if additional information or claim assessment is required for any specific claim request listed above!",
//     "pipeline_request": {
//       "HITL_Approved": "Yes",
//       "reason": "All documents and details are validated."
//     }
// }

const CoPilotChat = ({messages, messageLoader, sessionId, initialCustomerName, initialUserMessage, containerStyle, streamUrl, agentCd, plannerCd}) => {
  const [userQueryText, setUserQueryText] = useState('');
  const [showDelayMsg, setShowDelayMsg] = useState(false);
  const [localMessages, setLocalMessages] = useState(messages || []);
  const [processingSteps, setProcessingSteps] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentAgentId, setCurrentAgentId] = useState(null);
  const [streamDraft, setStreamDraft] = useState('');
  const currentAgentRef = useRef(null);
  const [sessionInternalId, setSessionInternalId] = useState(null);
  const initialSentRef = useRef(false);
  const evaluationRef = useRef(null);
  const [chatWidth, setChatWidth] = useState(436);
  const [userId, setUserId] = useState("");
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(436);
  const userData = localStorage.getItem('userData');
  const basePath = process.env.PUBLIC_URL || "";
  const textareaRef = useRef(null);

  useEffect(() => {
  if (!messageLoader) {
    setShowDelayMsg(false);
    return;
  }

  let delayTimer;
  let hideTimer;

  delayTimer = setTimeout(() => {
    setShowDelayMsg(true);

    hideTimer = setTimeout(() => {
      setShowDelayMsg(false);
    }, 3000);
  }, 5000);

  return () => {
    clearTimeout(delayTimer);
    clearTimeout(hideTimer);
  };
}, [messageLoader]);

useEffect(() => {
  try {
    const user = JSON.parse(userData);
    setUserId(user.username);
  } catch (error) {
    console.log("Error for userdata", error)
  }
},[])

  // Initialize local messages only on mount or when messages prop has actual content
  useEffect(() => {
    if (messages && messages.length > 0) {
      setLocalMessages(messages);
    }
  }, [messages]);

  // Reset chat state when agentCd changes (switching agents)
  useEffect(() => {
    if (agentCd) {
      setLocalMessages([]);
      setProcessingSteps([]);
      setStreamDraft('');
      setCurrentAgentId(null);
      currentAgentRef.current = null;
      setSessionInternalId(null);
      initialSentRef.current = false;
    }
  }, [agentCd]);

  // Auto-scroll to bottom whenever messages or streaming draft update
  const scrollEvaluationToBottom = (smooth = true) => {
    const el = evaluationRef.current;
    if (!el) return;
    try {
      // use requestAnimationFrame to wait for DOM updates
      requestAnimationFrame(() => {
        if (smooth && el.scrollTo) {
          el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        } else {
          el.scrollTop = el.scrollHeight;
        }
      });
    } catch (e) {
      el.scrollTop = el.scrollHeight;
    }
  };

  useEffect(() => {
    // scroll when number of messages changes or when streamDraft updates
    scrollEvaluationToBottom(true);
  }, [localMessages.length, streamDraft, isStreaming]);

  const handleStreamingMessage = (obj) => {
    // if (process.env.NODE_ENV === 'development') {
    //   console.debug('stream message', obj);
    // }
    // also log in production at info level to help debugging during testing
    // console.info && console.info('CoPilot stream message', obj);

    // Security: Do not trigger any pipeline execution from CoPilot chat.
    // If the backend includes a `pipeline_request` in stream objects, ignore it here.
    if (obj && obj.pipeline_request) {
      if (process.env.NODE_ENV === 'development') console.debug('pipeline_request received in stream - ignoring in CoPilotChat');
      // create a shallow copy without pipeline_request to avoid accidental usage downstream
      obj = { ...obj };
      delete obj.pipeline_request;
    }
    if (!obj) return;

    if (obj.type === 'processing') {
      // Mark previous in-progress as done and append new in-progress
      setProcessingSteps(prev => prev.map(s => s.status === 'in-progress' ? { ...s, status: 'done' } : s).concat([{ title: obj.title, status: 'in-progress' }]));
      return;
    }

    if (obj.type === 'final_response' || obj.type === 'partial_response') {
      const content = obj.content || '';
      // Append to stream draft
      setStreamDraft(prev => {
        const next = prev + content;
        // update the current agent message in localMessages
        const agentIdToUse = currentAgentRef.current || currentAgentId;
        if (agentIdToUse) {
          setLocalMessages(prevMsgs => prevMsgs.map(m => m.id === agentIdToUse ? { ...m, message: next } : m));
        } else {
          // fallback: update the last agent message in case agentId wasn't set yet
          setLocalMessages(prevMsgs => {
            const msgs = (prevMsgs || []).slice();
            let found = false;
            for (let i = msgs.length - 1; i >= 0; i--) {
              if (msgs[i].from === 'agent') {
                msgs[i] = { ...msgs[i], message: next };
                found = true;
                break;
              }
            }
            if (!found) {
              const newId = `agent-${Date.now()}`;
              currentAgentRef.current = newId;
              setCurrentAgentId(newId);
              msgs.push({ id: newId, from: 'agent', message: next });
            }
            return msgs;
          });
        }
        return next;
      });
      // NOTE: do NOT finalize on individual 'final_response' events - server may stream several chunks labeled 'final_response'.
      // Finalization will occur on 'stream_done' event.
    }

    if (obj.type === 'stream_done') {
      setIsStreaming(false);
      setProcessingSteps(prev => prev.map(s => s.status === 'in-progress' ? { ...s, status: 'done' } : s));
      setCurrentAgentId(null);
      currentAgentRef.current = null;
      // Optionally persist the message here, or let caller save on explicit action
    }
  };

  // Resize handlers for drag-to-expand
  const onResizeStart = (clientX) => {
    draggingRef.current = true;
    startXRef.current = clientX;
    startWidthRef.current = chatWidth;
    // Add global listeners
    document.addEventListener('mousemove', mouseMove);
    document.addEventListener('mouseup', mouseUp);
    document.addEventListener('touchmove', touchMove, { passive: false });
    document.addEventListener('touchend', touchEnd);
  };

  const mouseMove = (e) => {
    if (!draggingRef.current) return;
    const delta = startXRef.current - e.clientX; // drag left -> positive
    const maxW = Math.floor(window.innerWidth * 0.9);
    let newW = startWidthRef.current + delta;
    newW = Math.max(300, Math.min(newW, maxW));
    setChatWidth(newW);
  };

  const mouseUp = () => {
    draggingRef.current = false;
    document.removeEventListener('mousemove', mouseMove);
    document.removeEventListener('mouseup', mouseUp);
    document.removeEventListener('touchmove', touchMove);
    document.removeEventListener('touchend', touchEnd);
  };

  const touchMove = (e) => {
    if (!draggingRef.current) return;
    const clientX = e.touches[0].clientX;
    const delta = startXRef.current - clientX;
    const maxW = Math.floor(window.innerWidth * 0.9);
    let newW = startWidthRef.current + delta;
    newW = Math.max(300, Math.min(newW, maxW));
    setChatWidth(newW);
    e.preventDefault();
  };

  const touchEnd = () => mouseUp();

  useEffect(() => {
    return () => {
      // cleanup in case
      document.removeEventListener('mousemove', mouseMove);
      document.removeEventListener('mouseup', mouseUp);
      document.removeEventListener('touchmove', touchMove);
      document.removeEventListener('touchend', touchEnd);
    };
  }, []);

  // Create a new session id whenever the chat is opened for a (new) thread.
  // This ensures a fresh session per email open and stays consistent while the user remains on the screen.
  useEffect(() => {
    const baseId = sessionId || uuid();
    // let uuid = null;
    // try {
    //   uuid = (window && window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : null;
    // } catch (e) {
    //   uuid = null;
    // }
    // if (!uuid) uuid = `r${Date.now()}`;
    const sid = `${baseId}`;
    setSessionInternalId(sid);
    if (process.env.NODE_ENV === 'development') console.debug('CoPilot session created', sid);
    return () => {
      // clear session when component unmounts
      setSessionInternalId(null);
      if (process.env.NODE_ENV === 'development') console.debug('CoPilot session cleared', sid);
    };
  }, [sessionId]);

  const sendQuery = async (queryText) => {
    if (!queryText || queryText.trim() === '') return;
    // push user message
    const userMsg = { id: Date.now(), message: queryText, from: 'user' };
    setLocalMessages(prev => [...(prev || []), userMsg]);
    // clear input immediately for better UX
    setUserQueryText('');

    // create agent placeholder
    const agentId = `agent-${Date.now()}`;
    const agentMsg = { id: agentId, message: '', from: 'agent' };
    setLocalMessages(prev => [...(prev || []), agentMsg]);
    setCurrentAgentId(agentId);
    // set ref immediately to avoid race with streaming callbacks that may arrive synchronously
      currentAgentRef.current = agentId;
      setStreamDraft('');
      setIsStreaming(true);
      // reset processing steps for the new query
      setProcessingSteps([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = '40px'; // reset height
        // force padding for vertical centering
        textareaRef.current.style.paddingTop = '0.6rem';
        textareaRef.current.style.paddingBottom = '0.6rem';
      }

      try {
      const sid = sessionInternalId || sessionId || localStorage.getItem('emailThreadId') || 'session_test_01_034';
      console.info && console.info('sendQuery using session_id', sid);
      // Determine whether to send an agent_cd (default) or planner_cd based on provided props
      const codeToUse = plannerCd || agentCd || "AG-EXPLAINER";
      const codeFieldName = plannerCd ? 'planner_cd' : 'agent_cd';

      await streamAgentQuery({
        query: queryText,
        session_id: sid,
        user_id: userId,
        [codeFieldName]: codeToUse,
        payload: {}
      }, streamUrl, handleStreamingMessage);
    } catch (err) {
      console.error('Streaming error', err);
      setIsStreaming(false);
      // annotate agent message
      setLocalMessages(prev => prev.map(m => m.id === agentId ? { ...m, message: '[Error streaming response]' } : m));
      setCurrentAgentId(null);
    }
  };

  // Send an initial query once when the component mounts for a thread, if the
  // parent provided an `initialCustomerName`. Wait until a sessionInternalId is
  // created so the stream call uses a stable session id. Guard with
  // `initialSentRef` to avoid duplicate sends.
  useEffect(() => {
    if (!initialCustomerName) return;
    if (!sessionInternalId) return;
    if (initialSentRef.current) return;
    initialSentRef.current = true;

    const q = `show me information about ${initialCustomerName}`;
    try {
      // fire-and-forget; sendQuery handles errors and updates UI
      sendQuery(q);
    } catch (err) {
      console.error('Failed to send initial CoPilot query', err);
    }
  }, [initialCustomerName, sessionInternalId]);

  // If a parent supplied an explicit initial user message (e.g. the floating
  // chat 'Hi' button), send that once on mount instead of the
  // `initialCustomerName` query. We reuse `initialSentRef` so only one initial
  // auto-message is ever sent.
  useEffect(() => {
    if (!initialUserMessage) return;
    if (!sessionInternalId) return;
    if (initialSentRef.current) return;
    initialSentRef.current = true;

    try {
      sendQuery(initialUserMessage);
    } catch (err) {
      console.error('Failed to send initial user message', err);
    }
  }, [initialUserMessage, sessionInternalId]);

  const handleInputResize = (e) => {
  const textarea = e.target;

  textarea.style.height = '40px';
  if (textarea.scrollHeight > 40) {
    textarea.style.height = textarea.scrollHeight + 'px';
  }
};

// debug: showDelayMsg is used to display a delayed message when loader stalls

  return (
    <CoPilotContainer style={{ width: chatWidth, ...(containerStyle || {}) }}>
      <ResizeHandle
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize chat panel"
        onMouseDown={(e) => onResizeStart(e.clientX)}
        onTouchStart={(e) => onResizeStart(e.touches[0].clientX)}
        onDoubleClick={() => setChatWidth(436)}
      >
        <ResizeBar className="bar" />
      </ResizeHandle>
      {/* <CoPilotHeader>
        <CoPilotTitle>
          <div
            style={{
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <img src={`${basePath}/images/logo/meclaims-icon.png`} alt="logo" width={44} height={44} />
            <img src={`${basePath}/images/logo/meta-star.png`} alt="logo" style={{ position: 'absolute', top: '-6px', right: '-6px' }} width={16} height={16} />

          </div>
          MetaClaims Ai 
        </CoPilotTitle>
      </CoPilotHeader> */}

      {/* Processing stack area (dynamic) */}
      {processingSteps && processingSteps.length > 0 && (
        <StatusSection>
          {processingSteps.map((step, i) => (
            <StatusItem key={i}>
              <StatusIcon>
                {step.status === 'done' ? (
                  <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#4CAF50' }} />
                ) : step.status === 'in-progress' ? (
                  <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#2196F3' }} />
                ) : (
                  <FontAwesomeIcon icon={faCircleRight} style={{ color: '#9E9E9E' }} />
                )}
              </StatusIcon>
              <StatusContent>
                <StatusTitle>{step.title}</StatusTitle>
                {/* intentionally empty StatusText per UX request */}
              </StatusContent>
            </StatusItem>
          ))}
        </StatusSection>
      )}

      <EvaluationSection ref={evaluationRef}>
        {/* <EvaluationTitle>
          <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#4CAF50', fontSize: '1.2rem' }} />
          Evaluation Summary
        </EvaluationTitle>
        <EvaluationList>
          <EvaluationItem>
            <BulletPoint />
            <div>Coverage: <HighlightedText>Critical Illness Rider</HighlightedText></div>
          </EvaluationItem>
          <EvaluationItem>
            <BulletPoint />
            <div>Condition: <HighlightedText>Acute Myocardial Infarction</HighlightedText></div>
          </EvaluationItem>
          <EvaluationItem>
            <BulletPoint />
            <div>Policy Rule Applied: <HighlightedText>Waiting Period Requirement</HighlightedText></div>
          </EvaluationItem>
        </EvaluationList> */}
        {
          localMessages.length > 0 && (
            <>
              {
                localMessages.map((chatItem) => {
                  if (chatItem?.from == "user") {
                    return <UserMessageContainer key={chatItem.id}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {chatItem?.message}
                      </ReactMarkdown>
                    </UserMessageContainer>
                  } else {
                    return <MarkdownContainer key={chatItem.id} style={{ minHeight: 'auto', maxHeight: 'none', overflowY: 'initial', color: '#606060', height: 'auto' }}>
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          table: ({node, ...props}) => (
                            <TableScrollWrapper>
                              <table {...props} />
                            </TableScrollWrapper>
                          )
                        }}
                      >
                        {chatItem?.message}
                      </ReactMarkdown>
                    </MarkdownContainer>
                  }
                })
              }

              {/* show live streaming loader when active */}
              {isStreaming && (
                <div style={{ padding: '12px 16px' }}>
                  <ChatThinkingLoader />
                </div>
              )}
            </>
          )
        }
       
      </EvaluationSection>
      <LoadingSection>
       {messageLoader && <ChatThinkingLoader /> } 
       {showDelayMsg && <DelayMessage>Still working on it…</DelayMessage>}
      </LoadingSection>

      {/* <IneligibilitySection>
        <StatusItem>
          <StatusIcon>
            <FontAwesomeIcon icon={faTimesCircle} style={{ color: '#F44336', fontSize: '1.2rem' }} />
          </StatusIcon>
          <StatusContent>
            <StatusText>
              Based on the policy terms, the claim is not currently eligible for benefit payout.
            </StatusText>
          </StatusContent>
        </StatusItem>
        
        <ReasonBox>
          <StatusItem style={{ marginBottom: 0 }}>
            <StatusIcon>
              <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#757575', fontSize: '1.1rem' }} />
            </StatusIcon>
            <StatusContent>
              <StatusText>
                Reason: A 90-day waiting period applies before benefits can be admitted for this coverage. The waiting period has not yet been completed.
              </StatusText>
            </StatusContent>
          </StatusItem>
        </ReasonBox>
      </IneligibilitySection>

      <DraftSection>
        <StatusItem style={{ marginBottom: 0 }}>
          <StatusIcon>
            <FontAwesomeIcon icon={faStar} style={{ color: '#64B5F6', fontSize: '1.2rem' }} />
          </StatusIcon>
          <StatusContent>
            <StatusText>
              I've drafted an acknowledgement email explaining the eligibility outcome and the applicable waiting period in clear terms. <HighlightedText>Please review and confirm if you'd like to proceed with this response to the customer.</HighlightedText>
            </StatusText>
          </StatusContent>
        </StatusItem>
      </DraftSection> */}

      <ActionButton>
        {/* <PlusIcon style={{border:'1.5px solid #90caf9', padding:'2px', borderRadius:'50%'}}  icon={faPlus} /> */}
        <InputTextarea
          placeholder="How can I help you today?"
          value={userQueryText}
          ref={textareaRef}
          disabled={isStreaming || messageLoader}
          onChange={(e) => setUserQueryText(e.target.value)}
          onInput={handleInputResize} 
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              const currentVal = e.currentTarget.value || '';
              if (currentVal.trim() === '') {
                e.preventDefault();
                return;
              }
              e.preventDefault();
              sendQuery(currentVal);
              setUserQueryText(''); 
              e.currentTarget.style.height = '40px'; 
            }
          }}
        />
        <SendIconButton
          aria-label="Send message"
          onClick={() => sendQuery(userQueryText)}
          disabled={isStreaming || messageLoader || userQueryText.trim() === ''}
        >
          <FontAwesomeIcon icon={faCircleRight} />
        </SendIconButton>
      </ActionButton>
    </CoPilotContainer>
  );
};

export default CoPilotChat;
